// Small hand-rolled charts for the dashboard. No dependency. Single light
// theme — colours come from the app's CSS variables where possible.

import { useId, type ReactNode } from 'react';

export const CHART_COLORS = {
  good: 'var(--ok-fg)', //     #247b50
  goodSoft: '#bfe0cd', //      receding "healthy" fill
  warn: '#d98c2b',
  bad: 'var(--danger)', //     #b43b49
  neutral: '#d3dde6',
  youth: 'var(--blue)', //     #1769aa
  adults: '#8fbde0',
} as const;

export interface Segment {
  label: string;
  value: number;
  color: string;
}

export interface ExRow {
  label: string;
  /** Right-side callout — the exception the eye should land on. */
  exception?: { text: string; tone: 'warn' | 'bad' } | null;
  /** Quiet text shown when there's no exception (e.g. "24 ok"). */
  clear?: string;
  segments: Segment[];
  onClick?: () => void;
}

/** One thin bar per row with the exception surfaced on the right. Segment
 *  widths are proportional within the row; healthy fills are passed pale so
 *  amber/red read first. */
export function ExceptionBars({ rows, emptyNote }: { rows: ExRow[]; emptyNote?: string }) {
  const hasData = rows.some((r) => r.segments.some((s) => s.value > 0));
  if (!hasData) return <p className="dash-empty">{emptyNote ?? 'No data yet.'}</p>;

  return (
    <div className="exbars">
      {rows.map((r) => {
        const total = r.segments.reduce((n, s) => n + s.value, 0);
        const inner = (
          <>
            <span className="exbar-label">{r.label}</span>
            <span className="exbar-track" aria-hidden>
              {total === 0 ? (
                <span className="exbar-none">no records</span>
              ) : (
                r.segments
                  .filter((s) => s.value > 0)
                  .map((s) => (
                    <span
                      key={s.label}
                      className="exbar-seg"
                      style={{ flexGrow: s.value, background: s.color }}
                    />
                  ))
              )}
            </span>
            <span className="exbar-note">
              {r.exception ? (
                <span className={`exbar-ex exbar-ex-${r.exception.tone}`}>{r.exception.text}</span>
              ) : (
                <span className="exbar-clear">{r.clear ?? '—'}</span>
              )}
            </span>
          </>
        );
        return r.onClick ? (
          <button key={r.label} className="exbar exbar-click" onClick={r.onClick}>
            {inner}
          </button>
        ) : (
          <div key={r.label} className="exbar">
            {inner}
          </div>
        );
      })}
    </div>
  );
}

export function ChartLegend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <div className="chart-legend">
      {items.map((i) => (
        <span key={i.label} className="chart-legend-item">
          <span className="chart-legend-swatch" style={{ background: i.color }} />
          {i.label}
        </span>
      ))}
    </div>
  );
}

/** A single donut. `value` / `total` drive the arc; the centre shows the
 *  percentage, caption sits below. */
export function Donut({
  value,
  total,
  label,
  color = CHART_COLORS.good,
  rest = CHART_COLORS.neutral,
  size = 96,
}: {
  value: number;
  total: number;
  label: ReactNode;
  color?: string;
  rest?: string;
  size?: number;
}) {
  const pct = total > 0 ? value / total : 0;
  const box = 120;
  const stroke = 15;
  const r = (box - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = circ * pct;
  const titleId = useId();

  return (
    <figure className="chart-donut" style={{ width: size }}>
      <svg viewBox={`0 0 ${box} ${box}`} role="img" aria-labelledby={titleId}>
        <title id={titleId}>
          {typeof label === 'string' ? label : 'training'}:{' '}
          {total > 0 ? `${Math.round(pct * 100)}% (${value} of ${total})` : 'no data'}
        </title>
        <circle cx={box / 2} cy={box / 2} r={r} fill="none" stroke={rest} strokeWidth={stroke} />
        {total > 0 && (
          <circle
            cx={box / 2}
            cy={box / 2}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={circ / 4}
            strokeLinecap="round"
            transform={`rotate(-90 ${box / 2} ${box / 2})`}
          />
        )}
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="central"
          className="chart-donut-pct"
        >
          {total > 0 ? `${Math.round(pct * 100)}%` : '—'}
        </text>
      </svg>
      <figcaption>{label}</figcaption>
    </figure>
  );
}

export interface LinePoint {
  x: string; // label (month)
  y: number;
}
export interface LineSeries {
  label: string;
  color: string;
  points: LinePoint[];
}

/** Multi-series line chart over a shared, ordered x axis. Wide and shallow by
 *  default — meant to sit under a metric header. */
export function LineChart({
  series,
  height = 150,
  yLabel,
  emptyNote,
}: {
  series: LineSeries[];
  height?: number;
  yLabel?: string;
  emptyNote?: string;
}) {
  const xs = series[0]?.points.map((p) => p.x) ?? [];
  const allY = series.flatMap((s) => s.points.map((p) => p.y));
  const titleId = useId();

  if (xs.length < 2 || allY.length === 0) {
    return (
      <p className="dash-empty">
        {emptyNote ?? 'Not enough history yet — needs at least two months.'}
      </p>
    );
  }

  const w = 720;
  const padL = 40;
  const padR = 10;
  const padT = 8;
  const padB = 22;
  const maxY = Math.max(...allY, 1);
  const niceMax = Math.ceil(maxY / 10) * 10 || 10;
  const plotW = w - padL - padR;
  const plotH = height - padT - padB;
  const xAt = (i: number) => padL + (xs.length === 1 ? plotW / 2 : (i / (xs.length - 1)) * plotW);
  const yAt = (v: number) => padT + plotH - (v / niceMax) * plotH;
  const ticks = [0, niceMax / 2, niceMax];
  const every = Math.ceil(xs.length / 10 || 1);

  return (
    <figure className="chart-line">
      <svg viewBox={`0 0 ${w} ${height}`} role="img" aria-labelledby={titleId}>
        <title id={titleId}>
          {yLabel ?? 'Trend'} over {xs.length} months
        </title>
        {ticks.map((tk) => (
          <g key={tk}>
            <line x1={padL} y1={yAt(tk)} x2={w - padR} y2={yAt(tk)} stroke="var(--line)" />
            <text
              x={padL - 6}
              y={yAt(tk)}
              textAnchor="end"
              dominantBaseline="central"
              className="chart-axis"
            >
              {Math.round(tk)}
            </text>
          </g>
        ))}
        {xs.map((x, i) =>
          i % every === 0 || i === xs.length - 1 ? (
            <text key={x} x={xAt(i)} y={height - 6} textAnchor="middle" className="chart-axis">
              {x}
            </text>
          ) : null,
        )}
        {series.map((s) => {
          const d = s.points
            .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(p.y).toFixed(1)}`)
            .join(' ');
          return (
            <g key={s.label}>
              <path d={d} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" />
              {s.points.map((p, i) => (
                <circle key={i} cx={xAt(i)} cy={yAt(p.y)} r={2.5} fill={s.color} />
              ))}
            </g>
          );
        })}
      </svg>
      {series.length > 1 && (
        <ChartLegend items={series.map((s) => ({ label: s.label, color: s.color }))} />
      )}
    </figure>
  );
}
